// src/pages/Resources.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { FiSearch, FiExternalLink, FiFileText } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Common resource types (free-form; admins can type any value).
const RESOURCE_TYPES = ['Site Prep', 'User Manual', 'Installation Guide', 'Warranty', 'Spec Sheet', 'Other'];

function parseCats(str) {
  return (str || '').split(',').map(c => c.trim()).filter(Boolean);
}

function Resources() {
  const [resources, setResources] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Admin state
  const [adminPassword, setAdminPassword] = useState('');
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [editing, setEditing] = useState(null);

  // Bulk import + auto-organize
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [organizing, setOrganizing] = useState(false);
  const [organizeMsg, setOrganizeMsg] = useState(null);

  // Drive connection test
  const [driveTesting, setDriveTesting] = useState(false);
  const [driveResult, setDriveResult] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);

  const runDriveDiagnose = async () => {
    setDiagnosis('loading');
    try {
      const res = await axios.post(`${API_URL}/api/resources/admin/drive-diagnose`, { password: adminPassword });
      setDiagnosis(res.data.diagnosis || res.data);
    } catch (err) {
      setDiagnosis({ error: err.response?.data?.error || 'Diagnostic failed' });
    }
  };

  const runDriveTest = async () => {
    setDriveTesting(true);
    setDriveResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/resources/admin/drive-test`, { password: adminPassword });
      setDriveResult({ ok: true, ...res.data });
    } catch (err) {
      const d = err.response?.data || {};
      setDriveResult({ ok: false, error: d.error || 'Test failed', hint: d.hint, serviceAccountEmail: d.serviceAccountEmail });
    } finally {
      setDriveTesting(false);
    }
  };

  // Clear all resources (before rebuilding clean from Drive).
  const clearAllResources = async () => {
    if (!window.confirm('Delete ALL resources in the library? This cannot be undone. (Your Drive files are not affected.)')) return;
    try {
      const res = await axios.post(`${API_URL}/api/resources/admin/clear-all`, { password: adminPassword, confirm: true });
      setOrganizeMsg(res.data.message || 'Cleared.');
      await loadAll();
    } catch (err) {
      setOrganizeMsg(err.response?.data?.error || 'Could not clear');
    }
  };

  // Background rebuild job — runs server-side. We just start/stop/kill it and
  // poll its status, so it keeps going even if this tab closes.
  const [job, setJob] = useState(null);
  const [jobResumable, setJobResumable] = useState(false);
  const pollRef = useRef(null);

  const fetchJobStatus = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/resources/admin/rebuild/status`, { password: adminPassword });
      setJob(res.data.job || null);
      setJobResumable(!!res.data.resumable);
      // Stop polling once the job is no longer active.
      const st = res.data.job?.status;
      if (st && st !== 'running' && st !== 'stopping') {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        // Refresh the resource list when it finishes.
        if (st === 'done' || st === 'stopped') loadAll();
      }
    } catch { /* ignore transient poll errors */ }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    fetchJobStatus();
    pollRef.current = setInterval(fetchJobStatus, 3000);
  };

  // When the admin unlocks, check if a job is already running (e.g. started
  // earlier from another device) and show its progress.
  useEffect(() => {
    if (adminUnlocked) fetchJobStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUnlocked]);

  const startRebuild = async (resume = false) => {
    if (!resume && !window.confirm('Read every PDF from Drive, rename each file with an informative title, and rebuild the library? This runs on the server — you can close this tab and it keeps going. It renames your actual Drive files (original name kept in brackets).')) return;
    try {
      await axios.post(`${API_URL}/api/resources/admin/rebuild/start`, { password: adminPassword, resume });
      startPolling();
    } catch (err) {
      setOrganizeMsg(err.response?.data?.error || 'Could not start the rebuild');
    }
  };

  const controlRebuild = async (action) => {
    if (action === 'kill' && !window.confirm('Emergency kill: stop the rebuild immediately, even mid-file?')) return;
    try {
      await axios.post(`${API_URL}/api/resources/admin/rebuild/control`, { password: adminPassword, action });
      fetchJobStatus();
    } catch (err) {
      setOrganizeMsg(err.response?.data?.error || 'Could not send the command');
    }
  };

  const jobActive = job && (job.status === 'running' || job.status === 'stopping');

  // CSV parser that handles quoted fields with commas/newlines.
  const parseCSV = (text) => {
    const rows = []; let field = ''; let record = []; let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i]; const next = text[i + 1];
      if (inQuotes) {
        if (c === '"' && next === '"') { field += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else { field += c; }
      } else {
        if (c === '"') { inQuotes = true; }
        else if (c === ',') { record.push(field); field = ''; }
        else if (c === '\r') { /* ignore */ }
        else if (c === '\n') { record.push(field); rows.push(record); field = ''; record = []; }
        else { field += c; }
      }
    }
    if (field.length > 0 || record.length > 0) { record.push(field); rows.push(record); }
    return rows.filter(r => r.some(cell => (cell || '').trim() !== ''));
  };

  const buildImportRows = (csvRows) => {
    if (csvRows.length < 2) return [];
    const headers = csvRows[0].map(h => h.trim().toLowerCase());
    const idx = (names) => headers.findIndex(h => names.some(n => h.includes(n)));
    const ti = idx(['title', 'name', 'file']);
    const ui = idx(['url', 'link']);
    const di = idx(['description', 'desc']);
    const tyi = idx(['type']);
    const ci = idx(['category', 'categories']);
    const out = [];
    for (let r = 1; r < csvRows.length; r++) {
      const row = csvRows[r];
      const title = ti !== -1 ? (row[ti] || '').trim() : '';
      const url = ui !== -1 ? (row[ui] || '').trim() : '';
      if (!title || !url) continue;
      out.push({
        title, url,
        description: di !== -1 ? (row[di] || '').trim() : '',
        type: tyi !== -1 ? (row[tyi] || '').trim() : '',
        categories: ci !== -1 ? (row[ci] || '').trim() : ''
      });
    }
    return out;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(ev.target.result || '');
    reader.readAsText(file);
  };

  const runImport = async () => {
    setImportMsg(null);
    const rows = buildImportRows(parseCSV(importText));
    if (rows.length === 0) {
      setImportMsg('No valid rows found. The first line must be a header row with Title and Link (URL) columns.');
      return;
    }
    if (!window.confirm(`Import ${rows.length} resource${rows.length === 1 ? '' : 's'}?`)) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/resources/admin/import`, { password: adminPassword, rows });
      setImportMsg(res.data.message || 'Done.');
      setImportText('');
      await loadAll();
    } catch (err) {
      setImportMsg(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const runAutoOrganize = async () => {
    if (!window.confirm('Let Claude assign a type and categories to resources based on their titles? (Only fills in ones that are missing them.)')) return;
    setOrganizing(true);
    setOrganizeMsg(null);
    try {
      const res = await axios.post(`${API_URL}/api/resources/admin/auto-organize`, { password: adminPassword });
      setOrganizeMsg(res.data.message || 'Done.');
      await loadAll();
    } catch (err) {
      setOrganizeMsg(err.response?.data?.error || 'Auto-organize failed');
    } finally {
      setOrganizing(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [resRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/resources`),
        axios.get(`${API_URL}/api/questions/categories/list`).catch(() => ({ data: { categories: [] } }))
      ]);
      setResources(resRes.data.resources || []);
      setCategories(catRes.data.categories || []);
      // Fetch answered FAQs for cross-surfacing (related FAQs per resource).
      try {
        const faqRes = await axios.get(`${API_URL}/api/questions?status=answered`);
        const list = faqRes.data.questions || faqRes.data || [];
        setFaqs(Array.isArray(list) ? list : []);
      } catch { setFaqs([]); }
    } catch (err) {
      setError('Could not load resources.');
    } finally {
      setLoading(false);
    }
  };

  // Related FAQs for a resource = answered FAQs that share at least one category.
  const relatedFaqs = (resource) => {
    const rc = new Set(parseCats(resource.categories).map(c => c.toLowerCase()));
    if (rc.size === 0) return [];
    return faqs.filter(q => {
      const qc = parseCats(q.categories || q.category);
      return qc.some(c => rc.has(c.toLowerCase()));
    }).slice(0, 4);
  };

  const filtered = useMemo(() => {
    return resources.filter(r => {
      if (filterType !== 'all' && (r.type || '') !== filterType) return false;
      if (filterCategory !== 'all' && !parseCats(r.categories).includes(filterCategory)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.title} ${r.description || ''} ${r.type || ''} ${r.categories || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [resources, filterType, filterCategory, search]);

  const typesInUse = useMemo(() => {
    const s = new Set(resources.map(r => r.type).filter(Boolean));
    return [...s];
  }, [resources]);

  // --- Admin ---
  const handleUnlock = async () => {
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/verify`, { password: adminPassword });
      setAdminUnlocked(true);
      setShowAdminPrompt(false);
    } catch {
      setAdminError('Incorrect password');
    }
  };

  const saveResource = async () => {
    if (!editing.title?.trim() || !editing.url?.trim()) {
      setAdminError('Title and link are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/resources/admin/add`, {
        password: adminPassword,
        id: editing.id,
        title: editing.title,
        url: editing.url,
        description: editing.description || '',
        type: editing.type || '',
        categories: editing.categories || []
      });
      setEditing(null);
      await loadAll();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not save');
    }
  };

  const deleteResource = async (id) => {
    if (!window.confirm('Delete this resource? This cannot be undone.')) return;
    try {
      await axios.post(`${API_URL}/api/resources/admin/delete`, { password: adminPassword, id });
      await loadAll();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not delete');
    }
  };

  const toggleEditCategory = (name) => {
    const cur = Array.isArray(editing.categories) ? editing.categories : parseCats(editing.categories);
    setEditing({
      ...editing,
      categories: cur.includes(name) ? cur.filter(c => c !== name) : [...cur, name]
    });
  };

  if (loading) return <div className="text-center py-12 text-gray-600">Loading resources…</div>;
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600 mb-3">{error}</p>
        <button onClick={loadAll} className="bg-red-600 text-white px-4 py-2 rounded">Try Again</button>
      </div>
    );
  }

  const editCats = editing ? (Array.isArray(editing.categories) ? editing.categories : parseCats(editing.categories)) : [];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Resources</h2>
          <p className="text-gray-600">Manuals, site-prep sheets, warranty info, and guides. Click any resource to open it.</p>
        </div>
        <div className="ml-4 shrink-0">
          {!adminUnlocked ? (
            <button onClick={() => { setShowAdminPrompt(!showAdminPrompt); setAdminError(null); }}
              className="text-sm text-gray-500 hover:text-gray-800 underline">Admin</button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">Admin mode</span>
              <button onClick={() => setEditing({ categories: [] })} className="text-sm text-indigo-600 underline">Add resource</button>
              <button onClick={() => { setShowImport(!showImport); setImportMsg(null); }} className="text-sm text-indigo-600 underline">Bulk import</button>
              <button onClick={runAutoOrganize} disabled={organizing} className="text-sm text-indigo-600 underline disabled:opacity-50">
                {organizing ? 'Organizing…' : 'Auto-organize'}
              </button>
              <button onClick={runDriveTest} disabled={driveTesting} className="text-sm text-indigo-600 underline disabled:opacity-50">
                {driveTesting ? 'Testing…' : 'Test Drive connection'}
              </button>
              <button onClick={runDriveDiagnose} className="text-sm text-indigo-600 underline">
                Diagnose
              </button>
              {!jobActive ? (
                <>
                  <button onClick={() => startRebuild(false)} className="text-sm text-indigo-600 underline">
                    Rebuild from Drive
                  </button>
                  {jobResumable && (
                    <button onClick={() => startRebuild(true)} className="text-sm text-amber-700 underline font-medium">
                      Resume rebuild
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button onClick={() => controlRebuild('stop')} className="text-sm text-amber-700 underline">
                    Stop
                  </button>
                  <button onClick={() => controlRebuild('kill')} className="text-sm text-red-600 underline font-medium">
                    Kill
                  </button>
                </>
              )}
              <button onClick={clearAllResources} className="text-sm text-red-600 underline">
                Clear all
              </button>
              <button onClick={() => { setAdminUnlocked(false); setAdminPassword(''); }} className="text-sm text-gray-500 underline">Lock</button>
            </div>
          )}
        </div>
      </div>

      {showAdminPrompt && !adminUnlocked && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 max-w-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">Admin password</label>
          <input type="password" value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleUnlock(); }}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          {adminError && <p className="text-red-600 text-sm mt-2">{adminError}</p>}
          <button onClick={handleUnlock} className="mt-3 bg-indigo-600 text-white text-sm px-4 py-2 rounded">Unlock</button>
        </div>
      )}

      {organizeMsg && adminUnlocked && (
        <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded p-3 text-sm text-indigo-800">{organizeMsg}</div>
      )}

      {job && adminUnlocked && (
        <div className="mb-4 bg-white border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-900">
              {job.status === 'running' && 'Reading & renaming PDFs from Drive…'}
              {job.status === 'stopping' && 'Stopping…'}
              {job.status === 'stopped' && 'Rebuild stopped'}
              {job.status === 'done' && '✅ Rebuild complete'}
              {job.status === 'error' && '⚠️ Rebuild error'}
              {job.status === 'interrupted' && 'Rebuild interrupted (server restarted)'}
              {` — ${job.processed}/${job.total}`}
            </p>
            {jobActive && (
              <span className="text-xs text-gray-500">runs on the server · you can close this tab</span>
            )}
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div className="bg-indigo-600 h-2 rounded-full transition-all"
              style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }} />
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-2">
            <span className="text-green-700">✓ {job.renamed_count || 0} renamed</span>
            <span className="text-amber-700">⚠ {job.flagged_count || 0} flagged</span>
            <span className="text-red-600">✗ {job.error_count || 0} errors</span>
          </div>

          {jobActive && job.current_file && (
            <p className="text-xs text-gray-500 mb-2 truncate">Currently: {job.current_file}</p>
          )}

          {Array.isArray(job.recent_log) && job.recent_log.length > 0 && (
            <div className="max-h-48 overflow-y-auto text-xs space-y-1 border-t border-gray-100 pt-2">
              {job.recent_log.slice(-12).reverse().map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={
                    r.status === 'renamed' ? 'text-green-600' :
                    (r.status === 'unreadable' || r.status === 'too_large') ? 'text-amber-600' : 'text-red-600'
                  }>
                    {r.status === 'renamed' ? '✓' : (r.status === 'unreadable' || r.status === 'too_large') ? '⚠' : '✗'}
                  </span>
                  <span className="text-gray-700 truncate">{r.name}</span>
                  {r.status === 'unreadable' && <span className="text-amber-600">(couldn't identify)</span>}
                  {r.status === 'too_large' && <span className="text-amber-600">(too large)</span>}
                  {r.status === 'interrupted' && <span className="text-amber-600">(skipped after restart — check manually)</span>}
                  {r.status === 'error' && <span className="text-red-600">({r.error || 'error'})</span>}
                </div>
              ))}
            </div>
          )}

          {(job.status === 'done' || job.status === 'stopped' || job.status === 'interrupted' || job.status === 'error') && (() => {
            const attention = (Array.isArray(job.recent_log) ? job.recent_log : [])
              .filter(e => e && (e.status === 'unreadable' || e.status === 'too_large' || e.status === 'error' || e.status === 'interrupted'));
            return (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <p className="text-sm font-medium text-gray-900 mb-1">Report</p>
                <p className="text-xs text-gray-700">
                  {job.processed}/{job.total} processed — {job.renamed_count} renamed, {job.flagged_count} flagged, {job.error_count} errors.
                </p>
                {attention.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-amber-700 mb-1">Needs a manual name / attention ({attention.length}):</p>
                    <ul className="text-xs text-gray-700 list-disc list-inside max-h-40 overflow-y-auto">
                      {attention.map((e, i) => (
                        <li key={i}>{e.name}{e.error ? ` — ${e.error}` : (e.status === 'too_large' ? ' — too large to read' : " — couldn't identify")}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(job.status === 'stopped' || job.status === 'interrupted' || job.status === 'error') && jobResumable && (
                  <p className="text-xs text-amber-700 mt-2">Use <span className="font-medium">Resume rebuild</span> to finish the remaining files.</p>
                )}
                {job.status === 'done' && (
                  <p className="text-xs text-gray-500 mt-2">Tip: run <span className="font-medium">Auto-organize</span> next to assign types & categories.</p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {diagnosis && adminUnlocked && (
        <div className="mb-4 bg-gray-50 border border-gray-300 rounded p-3 text-xs">
          <p className="font-medium text-gray-800 mb-2">Drive diagnosis</p>
          {diagnosis === 'loading' ? (
            <p className="text-gray-600">Running…</p>
          ) : (
            <pre className="whitespace-pre-wrap break-all text-gray-700">{JSON.stringify(diagnosis, null, 2)}</pre>
          )}
        </div>
      )}

      {driveResult && adminUnlocked && (
        <div className={`mb-4 rounded p-3 text-sm border ${driveResult.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {driveResult.ok ? (
            <>
              <p className="font-medium">✅ Connected to Google Drive.</p>
              <p className="mt-1">Found <span className="font-medium">{driveResult.totalFiles}</span> file(s) ({driveResult.pdfCount} PDFs) in the folder.</p>
              {driveResult.sampleNames?.length > 0 && (
                <p className="mt-1 text-xs text-green-700">Sample: {driveResult.sampleNames.join(' · ')}</p>
              )}
            </>
          ) : (
            <>
              <p className="font-medium">⚠️ Could not connect: {driveResult.error}</p>
              {driveResult.serviceAccountEmail && (
                <p className="mt-1 text-xs">Share the folder (as Editor) with: <span className="font-mono">{driveResult.serviceAccountEmail}</span></p>
              )}
              {driveResult.hint && <p className="mt-1 text-xs">{driveResult.hint}</p>}
            </>
          )}
        </div>
      )}

      {showImport && adminUnlocked && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-1">Bulk import resources</h3>
          <p className="text-sm text-gray-600 mb-3">
            Upload or paste CSV. The first row must be a header row. Recognized columns:
            <span className="font-medium"> Title, Link (URL), Description, Type, Categories</span>.
            Title and Link are required; leave Type/Categories blank and use <span className="font-medium">Auto-organize</span> to fill them with AI.
          </p>
          <input type="file" accept=".csv,text/csv" onChange={handleFileUpload} className="block text-sm mb-3" />
          <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={6}
            placeholder="…or paste CSV here (including the header row)"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono mb-3" />
          {importMsg && <p className="text-sm text-indigo-700 mb-3">{importMsg}</p>}
          <div className="flex gap-2">
            <button onClick={runImport} disabled={importing || !importText.trim()}
              className="bg-indigo-600 text-white text-sm px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50">
              {importing ? 'Importing…' : 'Import'}
            </button>
            <button onClick={() => { setShowImport(false); setImportText(''); setImportMsg(null); }}
              className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100">Close</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <div className="relative">
          <FiSearch className="absolute left-3 top-3 text-gray-400" size={18} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search resources…"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="all">All Types</option>
          {typesInUse.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <p className="text-gray-600 mb-4 text-sm">{filtered.length} resource{filtered.length === 1 ? '' : 's'}</p>

      {filtered.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-600">
          {resources.length === 0 ? 'No resources yet. (Admins can add them with the “Add resource” button.)' : 'No resources match your filters.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const related = relatedFaqs(r);
            return (
              <div key={r.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a href={r.url} target="_blank" rel="noopener noreferrer"
                      className="text-lg font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-2">
                      <FiFileText className="shrink-0" /> {r.title} <FiExternalLink size={14} className="shrink-0" />
                    </a>
                    {r.description && <p className="text-gray-700 mt-1 text-sm">{r.description}</p>}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {r.type && <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded font-medium">{r.type}</span>}
                      {parseCats(r.categories).map(c => (
                        <span key={c} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">{c}</span>
                      ))}
                    </div>
                    {related.length > 0 && (
                      <div className="mt-3 border-t border-gray-100 pt-2">
                        <p className="text-xs font-medium text-gray-500 mb-1">Related FAQs</p>
                        <ul className="text-sm text-gray-700 list-disc list-inside space-y-0.5">
                          {related.map(q => (
                            <li key={q.id}>{q.normalized_question || q.original_question}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {adminUnlocked && (
                    <div className="flex gap-3 shrink-0">
                      <button onClick={() => setEditing(r)} className="text-xs text-indigo-600 underline">Edit</button>
                      <button onClick={() => deleteResource(r.id)} className="text-xs text-red-600 underline">Delete</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">{editing.id ? 'Edit resource' : 'Add resource'}</h3>

            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" placeholder="e.g. Residential Site Prep Requirements" />

            <label className="block text-sm font-medium text-gray-700 mb-1">Link (URL)</label>
            <input value={editing.url || ''} onChange={(e) => setEditing({ ...editing, url: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-1" placeholder="https://drive.google.com/…" />
            <p className="text-xs text-gray-500 mb-3">Paste the share link to your hosted file (Google Drive, Dropbox, your website, etc.). Make sure it's set so your team can view it.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              rows={2} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" />

            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <input list="resource-types" value={editing.type || ''} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" placeholder="e.g. Site Prep" />
            <datalist id="resource-types">
              {RESOURCE_TYPES.map(t => <option key={t} value={t} />)}
            </datalist>

            <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
            <p className="text-xs text-gray-500 mb-2">Pick the categories this belongs to — these connect it to related FAQs.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map(cat => {
                const selected = editCats.includes(cat.name);
                return (
                  <button key={cat.id || cat.name} type="button" onClick={() => toggleEditCategory(cat.name)}
                    className={`text-xs px-3 py-1 rounded-full border transition ${
                      selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                    }`}>
                    {selected ? '✓ ' : ''}{cat.name}
                  </button>
                );
              })}
            </div>

            {adminError && <p className="text-red-600 text-sm mb-2">{adminError}</p>}
            <div className="flex gap-2">
              <button onClick={saveResource} className="bg-indigo-600 text-white text-sm px-4 py-2 rounded">Save</button>
              <button onClick={() => setEditing(null)} className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Resources;
